import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import AuthLoader from './AuthLoader';
import DevAuthHint from './DevAuthHint';
import OpenInMessenger from './OpenInMessenger';
import { detectPlatformAsync, getPlatformName, PLATFORM_KINDS } from '../platforms';
import {
  clearPendingAccountLink,
  ensureAccountLinkCaptureReady,
  getPendingAccountLink,
  isLinkCodeProcessed,
  processPendingAccountLink,
  waitForPlatformAuthPayload,
} from '../utils/accountLinking';
import { getDevProviderUserId, isDevAuthEnabled } from '../utils/devAuth';
import authService from '../services/auth';
import { warmupMap } from '../utils/mapPreload';

const PlatformAuth = ({ children }) => {
  const {
    authenticate,
    authenticateDev,
    isLoading,
    isAuthenticated,
    refreshUser,
    applyLinkedUser,
  } = useAuth();
  const [platformKind, setPlatformKind] = useState(null);
  const [platformReady, setPlatformReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authAttempted, setAuthAttempted] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const linkInFlightRef = useRef(false);

  const handleDevAuth = useCallback(async () => {
    setAuthAttempted(true);
    const result = await authenticateDev();
    if (!result.success) {
      setAuthError(result.error || 'Debug authentication failed');
    }
  }, [authenticateDev]);

  const handlePlatformAuth = useCallback(async (platform) => {
    setAuthAttempted(true);
    try {
      const payload = await waitForPlatformAuthPayload(platform);

      if (!payload) {
        setAuthError(platform.getUnavailableMessage?.() || 'Authentication data not available');
        return;
      }

      const result = await authenticate(getPlatformName(platform), payload);

      if (!result.success) {
        setAuthError(result.error || 'Authentication failed');
      }
    } catch (error) {
      console.error('Platform authentication error:', error);
      setAuthError(error.message || 'Authentication failed');
    }
  }, [authenticate]);

  useEffect(() => {
    warmupMap();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const platform = await detectPlatformAsync();

      if (cancelled) {
        return;
      }

      const kind = platform ? getPlatformName(platform) : PLATFORM_KINDS.BROWSER;
      setPlatformKind(kind);

      if (!platform) {
        setPlatformReady(true);

        if (
          isDevAuthEnabled()
          && getDevProviderUserId()
          && !isAuthenticated
          && !isLoading
          && !authAttempted
        ) {
          handleDevAuth();
        }
        return;
      }

      await ensureAccountLinkCaptureReady(platform);

      if (cancelled) {
        return;
      }

      setPlatformReady(true);

      const platformName = getPlatformName(platform);
      const pendingLink = getPendingAccountLink();

      if (pendingLink?.linkToken && isLinkCodeProcessed(pendingLink.linkToken)) {
        clearPendingAccountLink();
      }

      const activePendingLink = getPendingAccountLink();
      if (
        activePendingLink
        && activePendingLink.targetProvider === platformName
        && activePendingLink.linkToken
        && !linkInFlightRef.current
      ) {
        linkInFlightRef.current = true;
        setIsLinking(true);
        try {
          const linkResult = await processPendingAccountLink(
            authService,
            platformName,
          );
          if (linkResult) {
            applyLinkedUser(linkResult);
            await refreshUser();
            setAuthAttempted(true);
            return;
          }
          if (authService.checkAuth()) {
            await refreshUser();
            setAuthAttempted(true);
            return;
          }
        } catch (error) {
          console.error('Pending account link failed:', error);
          setAuthError(error.message || 'Не удалось привязать аккаунт');
        } finally {
          linkInFlightRef.current = false;
          setIsLinking(false);
        }
      }

      if (cancelled) {
        return;
      }

      const hasPendingLink = Boolean(
        getPendingAccountLink()?.targetProvider === platformName,
      );

      if (
        !isAuthenticated
        && !isLoading
        && !authAttempted
        && !hasPendingLink
        && !linkInFlightRef.current
      ) {
        handlePlatformAuth(platform);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isLoading,
    authAttempted,
    handlePlatformAuth,
    handleDevAuth,
    refreshUser,
    applyLinkedUser,
  ]);

  const isDevBrowser = platformKind === PLATFORM_KINDS.BROWSER && isDevAuthEnabled();
  const devUserId = isDevBrowser ? getDevProviderUserId() : null;

  if (platformKind === null) {
    return <AuthLoader error={authError} />;
  }

  if (platformKind === PLATFORM_KINDS.BROWSER && !isDevAuthEnabled()) {
    return <OpenInMessenger />;
  }

  if (isDevBrowser && !devUserId && !isAuthenticated) {
    return <DevAuthHint error={authError} />;
  }

  if (!platformReady || isLoading || isLinking) {
    return <AuthLoader error={authError} />;
  }

  if (isDevBrowser && !isAuthenticated && authError) {
    return <DevAuthHint error={authError} />;
  }

  return children;
};

export default PlatformAuth;
